import { useCallback, useEffect, useState } from "react"
import { getRangeValue } from "../helpers/dateRange"
import { getUseSchoolYear } from "../helpers/flags"

export type FiltersState = Record<string, string[]>

export interface FilterSettings {
  [key: string]: { isSingleSelect?: boolean }
}

export const INITIAL_FILTERS_STATE: FiltersState = {
  date: getRangeValue?.("this school year")
}

export type UseFiltersState = {
  state: FiltersState
  stateSettings: FilterSettings
  openFilter: string | null
  setState: React.Dispatch<React.SetStateAction<FiltersState>>
  onSetFilter: (filter: string, value: string[]) => void
  onSetOpenFilter: (filter: string | null) => void
  onResetFilter: (filter: string | string[]) => void
  onAddFilter: (filter: string, defaultValue?: string[]) => void
  onRemoveFilter: (filter: string | string[]) => void
  onSetupFilters: (state: Partial<FiltersState>) => void
}

export const useFiltersState = (
  initialState?: Partial<FiltersState>
): UseFiltersState => {
  const [state, setState] = useState<FiltersState>({
    ...INITIAL_FILTERS_STATE,
    ...initialState
  })

  const [openFilter, setOpenFilter] = useState<string | null>(null)

  const onSetFilter = useCallback(
    (filter: string, value: string[]) => {
      setState(prevState => {
        const newState = {
          ...prevState,
          [filter]: value
        }
        return newState
      })
    },
    [state]
  )

  const onSetOpenFilter = useCallback((filter: string | null) => {
    setOpenFilter(filter)
  }, [])

  const onResetFilter = useCallback((filter: string | string[]) => {
    setState(prevState => {
      const filtersToReset = Array.isArray(filter) ? filter : [filter]
      const resetState = filtersToReset.reduce(
        (acc, filter) => ({
          ...acc,
          [filter]: []
        }),
        {}
      )

      return {
        ...prevState,
        ...resetState
      }
    })
  }, [])

  const onAddFilter = useCallback(
    (filter: string, defaultValue?: string[]) => {
      onSetFilter(filter, defaultValue)
    },
    [onSetFilter]
  )

  const onRemoveFilter = useCallback((filter: string | string[]) => {
    setState(prevState => {
      const newFilters = { ...prevState }

      const filtersToRemove = Array.isArray(filter) ? filter : [filter]
      filtersToRemove.forEach(filter => {
        delete newFilters[filter]
      })

      return newFilters
    })
  }, [])

  const onSetupFilters = useCallback((state: Partial<FiltersState>) => {
    setState(state)
  }, [])

  useEffect(() => {
    const dateRange = state.date

    if (!dateRange?.length && getUseSchoolYear() ) {
      onSetFilter("date", getRangeValue("this school year"))
    }
  }, [state, onSetFilter])

  return {
    state,
    stateSettings: {},
    openFilter,
    setState,
    onSetFilter,
    onSetOpenFilter,
    onResetFilter,
    onAddFilter,
    onRemoveFilter,
    onSetupFilters
  }
}
